import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useProjectStore } from '@/store/projectStore'
import { useProjectPermissions } from '@/auth/useProjectPermissions'
import { useAgentRefresh } from '@/hooks/useAgentRefresh'
import DiffViewer from '@/components/scm/DiffViewer'
import {
  getScmStatus, getScmBranches, getScmLog, getScmCommit, getScmDiff,
  scmFetch, scmPull, scmStage, scmUnstage, scmDiscard, scmCommit, scmPush,
  scmCreateBranch, scmCheckoutBranch, scmDeleteBranch, scmAbortMerge, scmResolveConflicts,
} from '@/api/workbench'
import { announceFlowRestoreStarted } from './FlowSetupDialog'
import { refreshFlowDesigns } from '@/api/integration'
import type { ScmStatus, ScmBranches, ScmCommit, ScmCommitDetail, ScmFileChange } from '@/types'

/**
 * Source Control tab — full git lifecycle for a project, reached from the
 * Advanced landing page. Stage/commit (selective), push/pull/fetch, branch
 * switch/create/delete, history browsing and per-file / per-commit diffs.
 * Merge conflicts surface as a banner: settle each file (Keep mine / Take
 * theirs / Mark resolved after editing it in the code editor), or all at once,
 * then commit — or abort. Every op is owner-scoped on the backend.
 */

type Selection =
  | { kind: 'file'; path: string; staged: boolean }
  | { kind: 'commit'; sha: string }
  | null

function errMsg(e: unknown, fallback: string): string {
  const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
  return detail || (e as Error)?.message || fallback
}

function StatusPill({ status }: { status?: string }) {
  const s = status || 'modified'
  const color =
    s.includes('delet') ? '#cf222e'
      : s === 'added' || s === 'untracked' ? '#1a7f37'
      : s === 'renamed' || s === 'copied' ? 'var(--color-accent)'
      : s.includes('both') || s.includes('_by_') ? '#bf8700'
      : 'var(--color-text-secondary)'
  return (
    <span style={{ fontSize: '0.8125rem', color, textTransform: 'capitalize', whiteSpace: 'nowrap' }}>
      {s.replace(/_/g, ' ')}
    </span>
  )
}

export default function SourceControlTab() {
  const { currentProject } = useProjectStore()
  const perms = useProjectPermissions()
  const navigate = useNavigate()
  const folder = currentProject?.application.folder_name

  const [status, setStatus] = useState<ScmStatus | null>(null)
  const [branches, setBranches] = useState<ScmBranches | null>(null)
  const [commits, setCommits] = useState<ScmCommit[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState<string | null>(null)
  const [commitMessage, setCommitMessage] = useState('')
  const [pullStrategy, setPullStrategy] = useState<'merge' | 'rebase'>('merge')
  const [selection, setSelection] = useState<Selection>(null)
  const [diffText, setDiffText] = useState('')
  const [commitDetail, setCommitDetail] = useState<ScmCommitDetail | null>(null)
  const [showNewBranch, setShowNewBranch] = useState(false)
  const [newBranchName, setNewBranchName] = useState('')
  const [refreshNonce, setRefreshNonce] = useState(0)

  const agentNonce = useAgentRefresh(folder, false)
  const canEdit = perms.canEditObjects
  const canPush = perms.canDeploy

  const reload = useCallback(async () => {
    if (!folder) return
    try {
      const [st, br] = await Promise.all([getScmStatus(folder), getScmBranches(folder)])
      setStatus(st)
      setBranches(br)
    } catch (e) {
      toast.error(errMsg(e, 'Failed to load git status'))
    } finally {
      setLoading(false)
    }
  }, [folder])

  const reloadLog = useCallback(async () => {
    if (!folder) return
    try {
      const { commits: cs } = await getScmLog(folder, 50, 0)
      setCommits(cs)
    } catch {
      /* log is best-effort */
    }
  }, [folder])

  useEffect(() => { setLoading(true); reload(); reloadLog() }, [reload, reloadLog, refreshNonce, agentNonce])

  // The app's IOFlow flow designs live in its source; write them now so a flow
  // built or edited in IOFlow shows here as a change (a push writes them too).
  useEffect(() => {
    if (!folder || !canEdit) return
    let cancelled = false
    refreshFlowDesigns(folder).then((changed) => {
      if (changed && !cancelled) setRefreshNonce((n) => n + 1)
    })
    return () => { cancelled = true }
  }, [folder, canEdit])

  // Refresh diff/commit detail when the selection changes.
  useEffect(() => {
    if (!folder || !selection) { setDiffText(''); setCommitDetail(null); return }
    if (selection.kind === 'file') {
      getScmDiff(folder, selection.path, selection.staged)
        .then((d) => { setDiffText(d.diff); setCommitDetail(null) })
        .catch((e) => toast.error(errMsg(e, 'Failed to load diff')))
    } else {
      getScmCommit(folder, selection.sha)
        .then((d) => { setCommitDetail(d); setDiffText(d.diff) })
        .catch((e) => toast.error(errMsg(e, 'Failed to load commit')))
    }
  }, [folder, selection])

  const run = useCallback(async (label: string, fn: () => Promise<void>) => {
    setBusy(label)
    try { await fn() } finally { setBusy(null) }
  }, [])

  if (!folder) return null

  if (loading) {
    return <div style={{ padding: '2rem', color: 'var(--color-text-secondary)' }}>Loading source control…</div>
  }

  if (!status?.is_repo) {
    return (
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '2rem' }}>
        <h2 style={{ marginTop: 0 }}>Source Control</h2>
        <p style={{ color: 'var(--color-text-secondary)' }}>
          This project is not connected to a git repository yet. Create a repository or
          push to one from the project <strong>Overview</strong> tab, then return here to
          manage commits, branches and history.
        </p>
        {status?.error && (
          <p style={{ color: '#cf222e', fontSize: '0.9125rem' }}>{status.error}</p>
        )}
      </div>
    )
  }

  const toggleStage = (f: ScmFileChange, staged: boolean) =>
    run('stage', async () => {
      try {
        if (staged) await scmUnstage(folder, [f.path])
        else await scmStage(folder, [f.path])
        await reload()
      } catch (e) { toast.error(errMsg(e, 'Stage failed')) }
    })

  const discardFile = (f: ScmFileChange) =>
    run('discard', async () => {
      if (!window.confirm(`Discard changes to ${f.path}? This cannot be undone.`)) return
      try {
        await scmDiscard(folder, [f.path])
        toast.success('Changes discarded')
        if (selection?.kind === 'file' && selection.path === f.path) setSelection(null)
        await reload()
      } catch (e) { toast.error(errMsg(e, 'Discard failed')) }
    })

  const doStageAll = () =>
    run('stage', async () => {
      const paths = [...(status.unstaged || []), ...(status.untracked || [])].map((f) => f.path)
      if (!paths.length) return
      try { await scmStage(folder, paths); await reload() }
      catch (e) { toast.error(errMsg(e, 'Stage failed')) }
    })

  const doCommit = () =>
    run('commit', async () => {
      // A rebase continues with each replayed commit's own message.
      const message = commitMessage.trim() || (status.operation === 'rebase' ? 'Continue rebase' : '')
      if (!message) { toast.error('Enter a commit message'); return }
      try {
        const r = await scmCommit(folder, message)
        toast.success(`Committed ${String(r.commit_hash || '').slice(0, 7)}`)
        setCommitMessage('')
        setSelection(null)
        await Promise.all([reload(), reloadLog()])
      } catch (e) { toast.error(errMsg(e, 'Commit failed')) }
    })

  const doFetch = () =>
    run('fetch', async () => {
      try { await scmFetch(folder); toast.success('Fetched from remote'); await reload() }
      catch (e) { toast.error(errMsg(e, 'Fetch failed')) }
    })

  const doPull = () =>
    run('pull', async () => {
      try {
        const r = await scmPull(folder, pullStrategy)
        if (r.conflict) {
          toast.error(`Merge conflict in ${r.conflicted_files?.length || 0} file(s)`)
        } else {
          toast.success(r.ioflow_restore?.started
            ? 'Pulled from remote — applying the pulled IOFlow flows (see Events & APIs › Flows)'
            : 'Pulled from remote')
          if (r.ioflow_restore?.started) announceFlowRestoreStarted(folder)
        }
        await Promise.all([reload(), reloadLog()])
      } catch (e) { toast.error(errMsg(e, 'Pull failed')) }
    })

  const doPush = () =>
    run('push', async () => {
      try {
        const r = await scmPush(folder)
        if (r.rejected) toast.error(r.reason || 'Push rejected — pull first')
        else toast.success('Pushed to remote')
        await reload()
      } catch (e) { toast.error(errMsg(e, 'Push failed')) }
    })

  const doCheckout = (name: string) => {
    if (!name || name === branches?.current) return
    run('checkout', async () => {
      try { await scmCheckoutBranch(folder, name); toast.success(`Switched to ${name}`); setSelection(null); await Promise.all([reload(), reloadLog()]) }
      catch (e) { toast.error(errMsg(e, 'Checkout failed')) }
    })
  }

  const doCreateBranch = () =>
    run('branch', async () => {
      if (!newBranchName.trim()) return
      try {
        await scmCreateBranch(folder, newBranchName.trim(), true)
        toast.success(`Created and switched to ${newBranchName.trim()}`)
        setShowNewBranch(false); setNewBranchName('')
        await Promise.all([reload(), reloadLog()])
      } catch (e) { toast.error(errMsg(e, 'Create branch failed')) }
    })

  const doDeleteBranch = (name: string) =>
    run('branch', async () => {
      if (!window.confirm(`Delete branch ${name}?`)) return
      try { await scmDeleteBranch(folder, name, false); toast.success(`Deleted ${name}`); await reload() }
      catch (e) {
        // Offer force-delete on unmerged branch.
        if (window.confirm(`${errMsg(e, 'Delete failed')}\n\nForce delete ${name}?`)) {
          try { await scmDeleteBranch(folder, name, true); toast.success(`Force-deleted ${name}`); await reload() }
          catch (e2) { toast.error(errMsg(e2, 'Delete failed')) }
        }
      }
    })

  const resolveConflicts = (paths: string[], side: 'mine' | 'theirs' | 'manual') =>
    run('resolve', async () => {
      if (!paths.length) return
      try {
        await scmResolveConflicts(folder, paths, side)
        toast.success(paths.length === 1 ? 'Conflict resolved' : `${paths.length} conflicts resolved`)
        if (selection?.kind === 'file' && paths.includes(selection.path)) setSelection(null)
        await reload()
      } catch (e) { toast.error(errMsg(e, 'Resolve failed')) }
    })

  const doAbort = () =>
    run('abort', async () => {
      try { await scmAbortMerge(folder); toast.success('Merge aborted'); setSelection(null); await Promise.all([reload(), reloadLog()]) }
      catch (e) { toast.error(errMsg(e, 'Abort failed')) }
    })

  const ahead = status.ahead ?? 0
  const behind = status.behind ?? 0
  const anyBusy = busy !== null
  // A merge/rebase whose conflicts are all settled — committing finishes it,
  // even when every file kept "mine" and nothing is staged.
  const finishing = !!status.operation

  const fileRow = (f: ScmFileChange, group: 'staged' | 'unstaged' | 'untracked' | 'conflicted') => {
    const isStaged = group === 'staged'
    const isConflict = group === 'conflicted'
    const isSel = selection?.kind === 'file' && selection.path === f.path && selection.staged === isStaged
    return (
      <div
        key={`${group}:${f.path}`}
        onClick={() => setSelection({ kind: 'file', path: f.path, staged: isStaged })}
        style={{
          display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.25rem 0.5rem',
          cursor: 'pointer', borderRadius: 4,
          background: isSel ? 'var(--color-bg-secondary)' : 'transparent', fontSize: '0.9125rem',
        }}
      >
        {!isConflict && (
          <input
            type="checkbox"
            checked={isStaged}
            disabled={!canEdit || anyBusy}
            onClick={(e) => e.stopPropagation()}
            onChange={() => toggleStage(f, isStaged)}
            title={isStaged ? 'Unstage' : 'Stage'}
          />
        )}
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={f.path}>
          {f.path}
        </span>
        <StatusPill status={f.status} />
        {isConflict && canEdit && (
          <>
            <span
              className="material-icons"
              title="Keep mine (your version)"
              onClick={(e) => { e.stopPropagation(); resolveConflicts([f.path], 'mine') }}
              style={{ fontSize: 17, color: 'var(--color-text-secondary)', cursor: anyBusy ? 'wait' : 'pointer' }}
            >
              person
            </span>
            <span
              className="material-icons"
              title="Take theirs (the pulled version)"
              onClick={(e) => { e.stopPropagation(); resolveConflicts([f.path], 'theirs') }}
              style={{ fontSize: 17, color: 'var(--color-text-secondary)', cursor: anyBusy ? 'wait' : 'pointer' }}
            >
              cloud_download
            </span>
            <span
              className="material-icons"
              title="Mark resolved (after editing it in the code editor)"
              onClick={(e) => { e.stopPropagation(); resolveConflicts([f.path], 'manual') }}
              style={{ fontSize: 17, color: 'var(--color-text-secondary)', cursor: anyBusy ? 'wait' : 'pointer' }}
            >
              check
            </span>
          </>
        )}
        {!isStaged && !isConflict && canEdit && (
          <span
            className="material-icons"
            title="Discard changes"
            onClick={(e) => { e.stopPropagation(); discardFile(f) }}
            style={{ fontSize: 17, color: 'var(--color-text-secondary)', cursor: 'pointer' }}
          >
            undo
          </span>
        )}
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 140px)', minHeight: 480, gap: '1rem', padding: '1rem' }}>
      {/* Left column */}
      <div style={{ width: 420, minWidth: 360, display: 'flex', flexDirection: 'column', gap: '0.75rem', overflowY: 'auto' }}>
        {/* Sync bar */}
        <div className="card" style={{ padding: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span className="material-icons" style={{ fontSize: 19, color: 'var(--color-accent)' }}>account_tree</span>
            <select
              value={branches?.current || ''}
              disabled={!canEdit || anyBusy}
              onChange={(e) => doCheckout(e.target.value)}
              style={{ flex: 1, fontSize: '0.9125rem', padding: '0.25rem' }}
              title="Switch branch"
            >
              {!branches?.local?.length && <option value="">{branches?.current || '—'}</option>}
              {branches?.local?.map((b) => (<option key={b} value={b}>{b}</option>))}
            </select>
            <span style={{ fontSize: '0.8625rem', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}
              title={`${ahead} ahead, ${behind} behind`}>
              ↑{ahead} ↓{behind}
            </span>
          </div>
          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
            <button className="btn btn-secondary" disabled={anyBusy} onClick={doFetch} style={{ fontSize: '0.8625rem' }}>
              {busy === 'fetch' ? 'Fetching…' : 'Fetch'}
            </button>
            <button className="btn btn-secondary" disabled={!canEdit || anyBusy} onClick={doPull} style={{ fontSize: '0.8625rem' }}>
              {busy === 'pull' ? 'Pulling…' : `Pull (${pullStrategy})`}
            </button>
            <select
              value={pullStrategy}
              disabled={!canEdit || anyBusy}
              onChange={(e) => setPullStrategy(e.target.value as 'merge' | 'rebase')}
              style={{ fontSize: '0.8125rem' }}
              title="Pull strategy"
            >
              <option value="merge">merge</option>
              <option value="rebase">rebase</option>
            </select>
            <button className="btn btn-secondary" disabled={!canPush || anyBusy} onClick={doPush} style={{ fontSize: '0.8625rem' }}>
              {busy === 'push' ? 'Pushing…' : 'Push'}
            </button>
            <button className="btn btn-secondary" disabled={!canEdit || anyBusy} onClick={() => setShowNewBranch((v) => !v)} style={{ fontSize: '0.8625rem' }}>
              New branch
            </button>
          </div>
          {showNewBranch && (
            <div style={{ display: 'flex', gap: '0.4rem' }}>
              <input
                autoFocus
                value={newBranchName}
                onChange={(e) => setNewBranchName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') doCreateBranch() }}
                placeholder="new-branch-name"
                style={{ flex: 1, fontSize: '0.9125rem', padding: '0.25rem' }}
              />
              <button className="btn btn-primary" disabled={anyBusy} onClick={doCreateBranch} style={{ fontSize: '0.8625rem' }}>Create</button>
            </div>
          )}
          {branches && branches.current && branches.local.filter((b) => b !== branches.current).length > 0 && (
            <details>
              <summary style={{ fontSize: '0.8425rem', color: 'var(--color-text-secondary)', cursor: 'pointer' }}>
                Manage branches
              </summary>
              <div style={{ marginTop: '0.4rem', display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                {branches.local.filter((b) => b !== branches.current).map((b) => (
                  <div key={b} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8825rem' }}>
                    <span style={{ flex: 1 }}>{b}</span>
                    <button className="btn btn-secondary" disabled={anyBusy} onClick={() => doCheckout(b)} style={{ fontSize: '0.8125rem', padding: '0.1rem 0.4rem' }}>Switch</button>
                    {canEdit && (
                      <button className="btn btn-secondary" disabled={anyBusy} onClick={() => doDeleteBranch(b)} style={{ fontSize: '0.8125rem', padding: '0.1rem 0.4rem' }}>Delete</button>
                    )}
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>

        {/* Conflict banner */}
        {status.has_conflicts && (
          <div className="card" style={{ padding: '0.75rem', borderLeft: '3px solid #cf222e' }}>
            <div style={{ fontWeight: 600, color: '#cf222e', marginBottom: '0.25rem' }}>
              Merge conflicts in {status.conflicted.length} file(s)
            </div>
            <p style={{ fontSize: '0.8625rem', color: 'var(--color-text-secondary)', margin: '0 0 0.5rem' }}>
              For each file keep your version, take the pulled one, or edit it in the code editor and mark it
              resolved — then commit{status.operation === 'rebase' ? ' (continues the rebase)' : ''}. Abort restores a clean tree.
            </p>
            {canEdit && (
              <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.4rem' }}>
                <button className="btn btn-secondary" disabled={anyBusy}
                  onClick={() => { if (window.confirm(`Keep your version of all ${status.conflicted.length} conflicted file(s)?`)) resolveConflicts(status.conflicted.map((f) => f.path), 'mine') }}
                  style={{ fontSize: '0.8625rem' }}>
                  Keep all mine
                </button>
                <button className="btn btn-secondary" disabled={anyBusy}
                  onClick={() => { if (window.confirm(`Take the pulled version of all ${status.conflicted.length} conflicted file(s)? Your local changes to them are discarded.`)) resolveConflicts(status.conflicted.map((f) => f.path), 'theirs') }}
                  style={{ fontSize: '0.8625rem' }}>
                  Take all theirs
                </button>
              </div>
            )}
            <div style={{ display: 'flex', gap: '0.4rem' }}>
              <button className="btn btn-secondary" onClick={() => navigate(`/srceditor/${encodeURIComponent(folder)}`)} style={{ fontSize: '0.8625rem' }}>
                Open in code editor
              </button>
              <button className="btn btn-secondary" disabled={anyBusy} onClick={doAbort} style={{ fontSize: '0.8625rem' }}>
                {busy === 'abort' ? 'Aborting…' : 'Abort'}
              </button>
            </div>
          </div>
        )}

        {/* Changes */}
        <div className="card" style={{ padding: '0.75rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
            <h3 style={{ margin: 0, fontSize: '1.0125rem', flex: 1 }}>Changes</h3>
            {((status.unstaged?.length || 0) + (status.untracked?.length || 0)) > 0 && canEdit && (
              <button className="btn btn-secondary" disabled={anyBusy} onClick={doStageAll} style={{ fontSize: '0.8125rem', padding: '0.1rem 0.5rem' }}>
                Stage all
              </button>
            )}
          </div>

          {status.conflicted?.length > 0 && (
            <>
              <div style={{ fontSize: '0.8125rem', color: '#cf222e', textTransform: 'uppercase', margin: '0.25rem 0' }}>Conflicted</div>
              {status.conflicted.map((f) => fileRow(f, 'conflicted'))}
            </>
          )}
          {status.staged?.length > 0 && (
            <>
              <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', textTransform: 'uppercase', margin: '0.4rem 0 0.25rem' }}>Staged ({status.staged.length})</div>
              {status.staged.map((f) => fileRow(f, 'staged'))}
            </>
          )}
          {status.unstaged?.length > 0 && (
            <>
              <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', textTransform: 'uppercase', margin: '0.4rem 0 0.25rem' }}>Changes ({status.unstaged.length})</div>
              {status.unstaged.map((f) => fileRow(f, 'unstaged'))}
            </>
          )}
          {status.untracked?.length > 0 && (
            <>
              <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', textTransform: 'uppercase', margin: '0.4rem 0 0.25rem' }}>Untracked ({status.untracked.length})</div>
              {status.untracked.map((f) => fileRow(f, 'untracked'))}
            </>
          )}
          {!status.staged?.length && !status.unstaged?.length && !status.untracked?.length && !status.conflicted?.length && (
            <div style={{ fontSize: '0.9125rem', color: 'var(--color-text-secondary)', padding: '0.5rem 0' }}>
              Working tree clean — nothing to commit.
            </div>
          )}

          {/* Commit box */}
          <div style={{ marginTop: '0.6rem' }}>
            <textarea
              value={commitMessage}
              onChange={(e) => setCommitMessage(e.target.value)}
              placeholder="Commit message (staged changes)"
              disabled={!canEdit || anyBusy}
              rows={2}
              style={{ width: '100%', fontSize: '0.9125rem', padding: '0.4rem', resize: 'vertical', boxSizing: 'border-box' }}
            />
            <button
              className="btn btn-primary"
              disabled={!canEdit || anyBusy || !!status.has_conflicts
                || (finishing ? (status.operation !== 'rebase' && !commitMessage.trim())
                  : (!commitMessage.trim() || !status.staged?.length))}
              onClick={doCommit}
              style={{ marginTop: '0.4rem', width: '100%', fontSize: '0.9125rem' }}
            >
              {busy === 'commit' ? 'Committing…'
                : finishing ? (status.operation === 'rebase' ? 'Continue rebase' : 'Commit merge')
                : `Commit ${status.staged?.length || 0} file(s)`}
            </button>
          </div>
        </div>

        {/* History */}
        <div className="card" style={{ padding: '0.75rem' }}>
          <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.0125rem' }}>History</h3>
          {!commits.length && <div style={{ fontSize: '0.8825rem', color: 'var(--color-text-secondary)' }}>No commits yet.</div>}
          {commits.map((c) => {
            const isSel = selection?.kind === 'commit' && selection.sha === c.hash
            return (
              <div
                key={c.hash}
                onClick={() => setSelection({ kind: 'commit', sha: c.hash })}
                style={{ padding: '0.3rem 0.4rem', cursor: 'pointer', borderRadius: 4, background: isSel ? 'var(--color-bg-secondary)' : 'transparent' }}
              >
                <div style={{ fontSize: '0.8925rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.subject}</div>
                <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                  {c.short_hash} · {c.author} · {new Date(c.date).toLocaleString()}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Right column: diff */}
      <div className="card" style={{ flex: 1, padding: 0, overflow: 'auto', minWidth: 0 }}>
        {!selection && (
          <div style={{ padding: '2rem', color: 'var(--color-text-secondary)' }}>
            Select a changed file or a commit to view its diff.
          </div>
        )}
        {selection?.kind === 'commit' && commitDetail && (
          <div style={{ padding: '0.75rem 0.75rem 0' }}>
            <div style={{ fontWeight: 600 }}>{commitDetail.subject}</div>
            <div style={{ fontSize: '0.8425rem', color: 'var(--color-text-secondary)', marginBottom: '0.5rem' }}>
              {commitDetail.short_hash} · {commitDetail.author} &lt;{commitDetail.email}&gt; · {new Date(commitDetail.date).toLocaleString()}
            </div>
            {commitDetail.body && <pre style={{ fontSize: '0.8625rem', whiteSpace: 'pre-wrap', margin: '0 0 0.5rem' }}>{commitDetail.body}</pre>}
          </div>
        )}
        {selection && <DiffViewer diff={diffText} />}
      </div>
    </div>
  )
}
